import {
	COMPLIMENTARY_REASON,
	getComplimentaryReason
} from './complimentaryProducts'

const addressFromForm = (form, billing = false) => {
	const useBilling = billing && form.otherBillingAddress
	const prefix = useBilling ? 'billing' : 'delivery'
	return {
		firstName: form[`${prefix}FirstName`],
		lastName: form[`${prefix}LastName`],
		company: form[`${prefix}Company`] || '',
		street: form[`${prefix}Street`],
		additionalInformation: form[`${prefix}AdditionalInformation`] || '',
		postalCode: form[`${prefix}ZipCode`],
		city: form[`${prefix}City`],
		country: form[`${prefix}Country`] || 'DE'
	}
}

const paymentMethodKey = (paymentOption) => {
	if (paymentOption === 'PayPal' || paymentOption === 'PayPal Express') return 'paypal'
	if (paymentOption === 'Werksverkauf') return 'pickup'
	return 'bank_transfer'
}

export const buildCheckoutPayload = ({
	deliveryForm,
	paymentOption,
	deliveryOption,
	cart,
	deliveryMessage,
	couponCode,
	paypalOrderId,
	affiliateId,
	quoteVersion,
	quoteToken
}) => ({
	email: deliveryForm.email,
	telephone: deliveryForm.telephone || '',
	paymentMethod: paymentMethodKey(paymentOption),
	paypalOrderId: paypalOrderId || '',
	deliveryOption: deliveryOption?.key || 'Standardversand',
	couponCode: couponCode || '',
	note: deliveryMessage || '',
	affiliateId: Number(affiliateId) || null,
	quoteVersion: quoteVersion || '',
	quoteToken: quoteToken || '',
	shippingAddress: addressFromForm(deliveryForm),
	billingAddress: addressFromForm(deliveryForm, true),
	products: cart
		.filter((item) => item.product_type === 'product')
		.map((item) => ({
			productId: item.product_strapi_id,
			variantId: item.product_variant_strapi_id,
			quantity: item.product_count
		})),
	complimentaryProducts: cart
		.filter(
			(item) =>
				item.product_type === 'freeProduct' &&
				(getComplimentaryReason(item) !==
					COMPLIMENTARY_REASON.BANK_TRANSFER ||
					paymentOption === 'Banküberweisung')
		)
		.map((item) => ({
			productId: item.product_strapi_id,
			variantId: item.product_variant_strapi_id,
			quantity: item.product_count
		})),
	bundles: cart
		.filter((item) => item.product_type === 'bundle')
		.map((item) => ({
			bundleId: item.product_strapi_id,
			variantIds: [
				item.product_variant_one_strapi_id,
				item.product_variant_two_strapi_id
			],
			quantity: item.product_count
		}))
})

const cents = (value) => Math.round(Number(value || 0) * 100)

export const applyQuoteToDeliveryOption = (deliveryOption, quote) =>
	deliveryOption
		? { ...deliveryOption, price: Number(quote?.shipping || 0) }
		: deliveryOption

const findQuoteLine = (item, quoteItems) =>
	quoteItems.find((line) => {
		if (item.product_type === 'bundle') {
			return (
				line.type === 'bundle' &&
				line.bundleId === item.product_strapi_id &&
				line.variantIds?.[0] === item.product_variant_one_strapi_id &&
				line.variantIds?.[1] === item.product_variant_two_strapi_id
			)
		}
		const expectedType =
			item.product_type === 'freeProduct' ? 'complimentaryProduct' : 'product'
		return (
			line.type === expectedType &&
			line.productId === item.product_strapi_id &&
			line.variantId === item.product_variant_strapi_id
		)
	})

export const getCheckoutPriceChanges = (
	cart,
	quote,
	expectedTotal,
	{ expectedShipping, expectedDiscount } = {}
) => {
	const changes = []
	for (const item of cart) {
		const line = findQuoteLine(item, quote?.items || [])
		if (!line) continue
		if (cents(item.product_variant_price) !== cents(line.unitPrice)) {
			changes.push({
				name: `${item.product_name}${line.variantName ? ` (${line.variantName})` : ''}`,
				oldPrice: Number(item.product_variant_price),
				newPrice: Number(line.unitPrice)
			})
		}
	}
	if (
		expectedShipping != null &&
		cents(expectedShipping) !== cents(quote?.shipping)
	) {
		changes.push({
			name:
				cents(expectedShipping) > 0 && cents(quote.shipping) === 0
					? 'Versandkosten (jetzt versandkostenfrei)'
					: 'Versandkosten',
			oldPrice: Number(expectedShipping),
			newPrice: Number(quote.shipping)
		})
	}
	if (
		expectedDiscount != null &&
		cents(expectedDiscount) !== cents(quote?.discount)
	) {
		changes.push({
			name: 'Rabatt',
			oldPrice: Number(expectedDiscount),
			newPrice: Number(quote.discount)
		})
	}
	if (expectedTotal != null && cents(expectedTotal) !== cents(quote?.total)) {
		changes.push({
			name: 'Gesamtsumme inklusive Versand und Rabatt',
			oldPrice: Number(expectedTotal),
			newPrice: Number(quote.total)
		})
	}
	return changes
}
